import { Box, Link, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import { GridRowParams, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { DataGridWithDefaultSort } from "components/DataGridWithDefaultSort";
import CopyableTypography from "components/Typography/CopyableTypography";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { orderBy } from "lodash";
import { prettyPrintDateTime, relativeLogTime } from "util/dates";
import { useCohortDetails } from "../useCohortDetails";
import { useState } from "react";

export function CohortJobs() {
  const theme = useTheme();
  const { data } = useCohortDetails();

  const workflows = data?.cohort?.workflows ?? [];

  const jobs = orderBy(
    workflows.flatMap((workflow: any) => {
      const activities = workflow.activities ?? [];
      return activities.flatMap((activity: any) => {
        const activityJobs = activity.jobs ?? [];
        return activityJobs.map((job: any) => ({
          ...job,
          workflowId: workflow.id,
          workflowStatus: workflow.status,
          workflowType: workflow.type,
          activityId: activity.id,
          activityType: activity.type,
          activityDescription: activity.description,
          activityStatus: activity.status,
        }));
      });
    }),
    ["createdAt"],
    ["desc"]
  );

  const [selectedJob, setSelectedJob] = useState(jobs?.[0]);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: "include",
      ids: new Set(selectedJob?.id),
    });

  const handleRowClick = (params: GridRowParams) => {
    setSelectedJob(params.row);
    setRowSelectionModel({ ids: new Set(params.row.id), type: "include" });
    // Also open in new tab for detailed view
    window.open(`/staff/jobs/${params.row.id}`, "_blank");
  };

  return (
    <Paper sx={{ paddingTop: theme.spacing(2) }} elevation={0}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
          padding: theme.spacing(2),
          paddingTop: 0,
          minHeight: "500px",
        }}
      >
        {/* Jobs Section */}
        <Box>
          <Typography paddingLeft={theme.spacing(1)} variant="h6">
            Jobs
          </Typography>
          <DataGridWithDefaultSort
            autoHeight
            hideFooter
            rows={jobs}
            sortModel={[{ field: "createdAt", sort: "desc" }]}
            rowSelectionModel={rowSelectionModel}
            onRowClick={handleRowClick}
            columns={[
              {
                field: "id",
                headerName: "ID",
                width: 250,
                renderCell: ({ row }) => (
                  <CopyableTypography variant="body2" text={row.id} />
                ),
              },
              {
                field: "task",
                headerName: "Task",
                width: 160,
                renderCell: ({ row }) => {
                  return row.task ? prettyPrintEnumValue(row.task) : null;
                },
              },
              { field: "status", headerName: "Status" },
              {
                field: "activityDescription",
                headerName: "Activity",
                flex: 1,
                minWidth: 150,
                renderCell: ({ row }) => (
                  <Tooltip title={row.activityDescription}>
                    <span
                      style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {row.activityDescription}
                    </span>
                  </Tooltip>
                ),
              },
              {
                field: "createdAt",
                headerName: "Created At",
                width: 160,
                renderCell: ({ row }) => (
                  <Tooltip title={prettyPrintDateTime(row.createdAt)}>
                    <span>{relativeLogTime(row.createdAt)}</span>
                  </Tooltip>
                ),
              },
            ]}
          />
        </Box>

        {/* Logs Section */}
        <Box sx={{ mb: theme.spacing(3) }}>
          <Typography paddingLeft={theme.spacing(1)} variant="h6">
            Logs for {selectedJob ? selectedJob.id : "No job selected"}
          </Typography>
          <DataGridWithDefaultSort
            sx={{ maxHeight: "300px" }}
            hideFooter
            rows={[]}
            columns={[
              {
                field: "message",
                headerName: "Message",
                flex: 1,
                renderCell: () => (
                  <Typography variant="body2" color="text.secondary">
                    No logs available for cohort workflow jobs
                  </Typography>
                ),
              },
            ]}
          />
        </Box>

        {/* Steps Done Section */}
        <Box>
          <Typography paddingLeft={theme.spacing(1)} variant="h6">
            Steps Done
          </Typography>
          <DataGridWithDefaultSort
            autoHeight
            hideFooter
            rows={[]}
            columns={[
              {
                field: "message",
                headerName: "Message",
                flex: 1,
                renderCell: () => (
                  <Typography variant="body2" color="text.secondary">
                    No steps data available for cohort workflow jobs
                  </Typography>
                ),
              },
            ]}
          />
        </Box>
      </Box>
    </Paper>
  );
}
